import { createRouter, lazyRouteComponent } from "@tanstack/react-router"

import NotFoundRedirect from "#Components/NotFoundRedirect"

import { rootRoute } from "./rootRoute"
import { ROUTE_TREE } from "./routes"

// The root route renders the application shell (`RootLayout` → `<Outlet/>`).
// `notFoundComponent` reloads to Rails for URLs the React app doesn't own.
//
// `RootLayout` is loaded lazily like the leaf routes. This keeps the route
// tree's static import graph thin because eager shell imports affect every
// module that imports `routeTree`, including the test harness via `utils/router`.
// `tanstackRouter.load()` resolves the component before the first render, so
// there is no boot flash.
rootRoute.update({
  component: lazyRouteComponent(() => import("#Components/RootLayout")),
  notFoundComponent: NotFoundRedirect,
})

const routeTree = rootRoute.addChildren(ROUTE_TREE)

// Mirror the legacy URL encoding: every value is a string, `+` decodes to space,
// keys & values are URI-decoded. Passed to `createRouter` as its `parseSearch`
// so search decoding matches the query-string handling the legacy router used.
export const parseSearch = (searchStr: string): Record<string, string> => {
  const params: Record<string, string> = {}
  const cleaned = searchStr.startsWith("?") ? searchStr.slice(1) : searchStr
  if (!cleaned) return params
  for (const part of cleaned.split("&")) {
    if (!part) continue
    const eqIdx = part.indexOf("=")
    const rawKey = eqIdx === -1 ? part : part.substring(0, eqIdx)
    const rawValue = eqIdx === -1 ? "" : part.substring(eqIdx + 1)
    const key = decodeURIComponent(rawKey.replace(/\+/g, " "))
    const value = decodeURIComponent(rawValue.replace(/\+/g, " "))
    params[key] = value
  }
  return params
}

// The inverse of `parseSearch`, and the serializer every router-built URL goes
// through. Exported so code that assembles a URL string by hand (see
// `useRouter.navigateWithParams`) encodes its query the same way the router
// does, rather than reaching for `URLSearchParams` — which would emit `+` for
// spaces where this emits `%20`.
export const stringifySearch = (search: Record<string, unknown>): string => {
  const parts: string[] = []
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null) continue
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
  }
  return parts.length > 0 ? `?${parts.join("&")}` : ""
}

export const tanstackRouter = createRouter({
  routeTree,
  // "never", not "preserve": 11 routes are declared `path: "/"` under a layout
  // (the org index, the four incident detail pages, the check-in and traces
  // action detail pages), so their `fullPath` carries a trailing slash. Under
  // "preserve" any router-built URL for those routes gained a slash the legacy
  // URL never had — `navigateWithParams({ overlay: null })` on an incident page
  // turned `…/incidents/123` into `…/incidents/123/`. Incoming URLs still match
  // with or without one.
  trailingSlash: "never",
  // Keep ISO timestamp colons readable in path params, as the legacy router did.
  pathParamsAllowedCharacters: [":"],
  defaultPreload: false,
  parseSearch,
  stringifySearch,
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof tanstackRouter
  }
}

export default tanstackRouter
